import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  applyHeadMeta,
  upsertJsonLd,
  getPageOgImage,
  isPrerenderedFor,
  removePrerenderedJsonLd,
} from '../lib/headMeta';
import { buildSchema, resolveSEO } from '../lib/seoSchema';
import { useCMSContent } from './useCMSContent';

function injectJsonLd(schemas: Record<string, unknown>[]) {
  // Upsert each route-level block by a stable id (page-0, page-1, …). On a
  // prerendered page those ids already exist (the static HTML marks them
  // data-prerendered="1"), so this REWRITES the existing tags in place rather
  // than appending duplicates. Page components own their own blocks
  // (e.g. 'article', 'jobposting') and are left untouched here.
  schemas.forEach((schema, i) => upsertJsonLd(`page-${i}`, schema));
  // Prune any leftover page-N blocks from a previous, longer route.
  document.head.querySelectorAll('script[data-seo-jsonld^="page-"]').forEach((s) => {
    const id = s.getAttribute('data-seo-jsonld') || '';
    const n = Number(id.slice('page-'.length));
    if (!Number.isInteger(n) || n >= schemas.length) s.remove();
  });
}

function setRobots(content: string | null) {
  const existing = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (!content) {
    existing?.remove();
    return;
  }
  if (existing) {
    existing.setAttribute('content', content);
  } else {
    const el = document.createElement('meta');
    el.setAttribute('name', 'robots');
    el.setAttribute('content', content);
    document.head.appendChild(el);
  }
}

/**
 * Drives per-route head metadata (title, description, keywords, canonical,
 * Open Graph / Twitter) plus JSON-LD (Organization, LocalBusiness,
 * BreadcrumbList, page entity) for every page.
 */
export function useSEO() {
  const { pathname } = useLocation();
  const { settings } = useCMSContent();
  const defaultOgImage = settings?.seo?.ogImage as string | undefined;

  useEffect(() => {
    if (pathname.startsWith('/admin')) return;
    // Creators Cut sets its own title and noindex.
    if (pathname.startsWith('/creators-cut')) return;
    // Dynamic Local Service Pages (/service-areas/{city}[/{service}]) own their own
    // head + structured data (useLocalPageSeo) so a page-specific title, Service
    // JSON-LD and per-page FAQ can be applied. The /service-areas directory
    // itself still uses the standard pass.
    if (pathname.startsWith('/service-areas/')) return;

    const meta = resolveSEO(pathname);
    const noIndex = ['/privacy-policy', '/terms-of-service', '/search'].includes(pathname);
    // A page may register its own social image (e.g. an article hero); that
    // always wins over the site-wide default.
    const ogImage = getPageOgImage(pathname) || meta.ogImage || defaultOgImage;
    // The prerendered HTML already ships this route's title, description,
    // canonical and OG tags. Don't overwrite them on first load (a detail page
    // may have set a more specific title); a later client-side navigation
    // re-enables the full head pass.
    const prerendered = isPrerenderedFor(pathname);

    if (noIndex) {
      setRobots('noindex, follow');
      // A noindex page must ship no JSON-LD. Drop any static blocks and never
      // add our own.
      removePrerenderedJsonLd();
      document.head.querySelectorAll('script[data-seo-jsonld]').forEach((s) => s.remove());
      if (!prerendered) {
        document.title = meta.title;
        applyHeadMeta({ title: meta.title, description: meta.description, path: pathname, ogImage });
      }
      return;
    }

    setRobots(null);

    if (!prerendered) {
      // We own the head now — drop any stale static JSON-LD from a previous route.
      removePrerenderedJsonLd();
      applyHeadMeta({
        title: meta.title,
        description: meta.description,
        keywords: meta.keywords,
        path: pathname,
        ogImage,
      });
    }

    // JSON-LD is reconciled on every load, but as an in-place update: on a
    // prerendered page the static blocks share our ids, so this rewrites them
    // instead of injecting a second copy.
    injectJsonLd(buildSchema(meta, pathname));
  }, [pathname, defaultOgImage]);
}